import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, Loader2, CheckCircle2, AlertCircle, ExternalLink, CreditCard, Wallet, Check } from 'lucide-react';
import { usePaymentSettings, useSavePaymentSettings, type PaymentSettings } from '../../../hooks/useManagement';
import { useToast } from '../../../components/ToastProvider';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import SaveButton from '../../../components/SaveButton';

interface ConnectStatus {
  connected: boolean;
  charges_enabled: boolean;
  details_submitted?: boolean;
  status: string | null;
}

type LoadState = 'loading' | 'ready' | 'error';

const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm';

// Owner Stripe Connect / payouts management AND the single place to configure HOW
// payments are received (tenant-level, applied to ALL the owner's properties).
// requireClientAuth (portal). On return from Stripe onboarding (return_url =
// /portal/manage/payouts) we just re-fetch the status on mount.
export default function ManagePayoutsPage() {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>('loading');
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = async () => {
    setState('loading');
    try {
      const res = await fetch('/api/portal/connect/status', { credentials: 'same-origin' });
      if (!res.ok) {
        setState('error');
        return;
      }
      const data = (await res.json()) as ConnectStatus;
      setStatus(data);
      setState('ready');
    } catch {
      setState('error');
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const onConnect = async () => {
    setError('');
    setConnecting(true);
    try {
      const res = await fetch('/api/portal/connect/onboard', {
        method: 'POST',
        credentials: 'same-origin',
      });
      const data = (await res.json()) as { url?: string; error?: string; message?: string };
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      // Surface the real Stripe/Connect reason so it's diagnosable.
      setError(data.message || data.error || t('portal.manage.payouts.errorStart'));
    } catch {
      setError(t('portal.manage.payouts.errorServer'));
    } finally {
      setConnecting(false);
    }
  };

  const isActive = status?.connected && status.charges_enabled;
  const isPending = status?.connected && !status.charges_enabled;
  const notConnected = !status?.connected;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Banknote size={24} className="text-teal-600" /> {t('portal.manage.payouts.title')}
        </h1>
        <p className="text-slate-500 mt-1">
          {t('portal.manage.payouts.subtitle')}
        </p>
      </div>

      {state === 'loading' && (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-teal-500" size={32} />
        </div>
      )}

      {state === 'error' && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700 flex items-center justify-between">
          <span>{t('portal.manage.payouts.errorLoad')}</span>
          <button onClick={loadStatus} className="font-medium underline hover:no-underline">{t('portal.manage.payouts.retry')}</button>
        </div>
      )}

      {state === 'ready' && (
        <>
          {/* ── Connect Stripe ── */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-4">
            <div>
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <CreditCard size={18} className="text-teal-600" /> {t('portal.manage.payouts.connectTitle', 'Conecta Stripe')}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {t('portal.manage.payouts.connectIntro', 'Cobra con tarjeta, Bizum y Apple/Google Pay al instante. 0% de comisión de Kanarix.')}
              </p>
            </div>

            {/* NOT connected */}
            {notConnected && (
              <div>
                <p className="text-slate-600 leading-relaxed">
                  {t('portal.manage.payouts.notConnectedBody')}
                </p>
                {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
                <button
                  onClick={onConnect}
                  disabled={connecting}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white font-semibold transition-colors"
                >
                  {connecting ? <Loader2 size={18} className="animate-spin" /> : <ExternalLink size={18} />}
                  {t('portal.manage.payouts.connectStripe')}
                </button>
              </div>
            )}

            {/* PENDING — connected but charges not enabled */}
            {isPending && (
              <div>
                <div className="flex items-start gap-3">
                  <AlertCircle size={22} className="text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900">{t('portal.manage.payouts.pendingTitle')}</p>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                      {t('portal.manage.payouts.pendingBody')}
                    </p>
                  </div>
                </div>
                {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
                <button
                  onClick={onConnect}
                  disabled={connecting}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white font-semibold transition-colors"
                >
                  {connecting ? <Loader2 size={18} className="animate-spin" /> : <ExternalLink size={18} />}
                  {t('portal.manage.payouts.continueStripe')}
                </button>
              </div>
            )}

            {/* ACTIVE */}
            {isActive && (
              <div className="flex items-start gap-3">
                <CheckCircle2 size={24} className="text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-700">{t('portal.manage.payouts.activeTitle')}</p>
                  <p className="mt-1 text-sm text-slate-600">{t('portal.manage.payouts.activeBody')}</p>
                </div>
              </div>
            )}
          </section>

          {/* ── How do you want to receive payments? ── */}
          <PaymentSettingsSection stripeActive={!!isActive} />
        </>
      )}
    </div>
  );
}

// Tenant-level payment config: how the owner receives payments + deposit + tax.
// Applies to ALL the owner's properties (replaces the old per-property "Pagos").
function PaymentSettingsSection({ stripeActive }: { stripeActive: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading } = usePaymentSettings();
  const saveMut = useSavePaymentSettings();
  const toast = useToast();
  const [form, setForm] = useState<PaymentSettings | null>(null);
  const { isDirty, resetBaseline } = useDirtyForm(form);

  useEffect(() => {
    if (data && !form) { setForm(data); resetBaseline(data); }
  }, [data, form, resetBaseline]);

  if (isLoading || !form) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="animate-spin text-teal-500" size={24} />
      </div>
    );
  }

  const set = <K extends keyof PaymentSettings>(k: K, v: PaymentSettings[K]) => {
    setForm(f => (f ? { ...f, [k]: v } : f));
  };
  const num = (v: string): number | null => (v === '' ? null : Number(v));

  const mode = form.payment_mode;
  const showTransfer = mode === 'transfer' || mode === 'both';
  const showStripe = mode === 'stripe' || mode === 'both';

  const onSave = async () => {
    try {
      await saveMut.mutateAsync(form);
      resetBaseline(form);
      toast.success(t('common.savedToast', 'Cambios guardados'));
    } catch {
      toast.error(t('common.saveError', 'No se ha podido guardar. Inténtalo de nuevo.'));
    }
  };

  const ModeOption = ({ value, icon: Icon, title, desc }: {
    value: PaymentSettings['payment_mode']; icon: typeof CreditCard; title: string; desc: string;
  }) => {
    const on = mode === value;
    return (
      <button type="button" onClick={() => set('payment_mode', value)}
        className={`text-left rounded-xl border p-4 transition-colors ${
          on ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500' : 'border-slate-200 bg-white hover:border-teal-300'
        }`}>
        <div className="flex items-center gap-2">
          <Icon size={18} className={on ? 'text-teal-600' : 'text-slate-400'} />
          <span className="font-semibold text-slate-900 text-sm">{title}</span>
          {on && <Check size={16} className="text-teal-600 ml-auto" />}
        </div>
        <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{desc}</p>
      </button>
    );
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
      <div>
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <Wallet size={18} className="text-teal-600" /> {t('portal.manage.payouts.howReceiveTitle', '¿Cómo quieres recibir los pagos?')}
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {t('portal.manage.payouts.howReceiveSub', 'Esta configuración se aplica a todos tus alojamientos.')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ModeOption value="stripe" icon={CreditCard}
          title={t('portal.manage.payouts.modeStripe', 'Stripe')}
          desc={t('portal.manage.payouts.modeStripeDesc', 'Cobro al instante con tarjeta, Bizum y wallets.')} />
        <ModeOption value="transfer" icon={Banknote}
          title={t('portal.manage.payouts.modeTransfer', 'Transferencia bancaria')}
          desc={t('portal.manage.payouts.modeTransferDesc', 'El huésped reserva y tú confirmas al recibir la transferencia.')} />
        <ModeOption value="both" icon={Wallet}
          title={t('portal.manage.payouts.modeBoth', 'Ambos')}
          desc={t('portal.manage.payouts.modeBothDesc', 'El huésped elige cómo pagar.')} />
      </div>

      {showStripe && !stripeActive && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          {t('portal.manage.payouts.stripeNeeded', 'Para cobrar con Stripe necesitas conectar tu cuenta arriba.')}
        </div>
      )}

      {showTransfer && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.payouts.fieldIban', 'IBAN')}</span>
            <input className={input} value={form.bank_iban ?? ''} onChange={e => set('bank_iban', e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.payouts.fieldBankHolder', 'Titular de la cuenta')}</span>
            <input className={input} value={form.bank_holder ?? ''} onChange={e => set('bank_holder', e.target.value)} />
          </label>
        </div>
      )}

      {/* Deposit / tourist tax — tenant-wide defaults */}
      <div className="border-t border-slate-100 pt-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.payouts.fieldDepositPercent', 'Depósito / señal online (%)')}</span>
            <input type="number" min={0} max={100} className={input} value={form.deposit_percent ?? ''} onChange={e => set('deposit_percent', num(e.target.value))} placeholder={t('portal.manage.payouts.depositPlaceholder', '0 = importe completo')} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">{t('portal.manage.payouts.fieldTouristTaxPerNight', 'Tasa turística (€/noche)')}</span>
            <input type="number" min={0} step="0.01" className={input} value={form.tourist_tax_per_night ?? ''} onChange={e => set('tourist_tax_per_night', num(e.target.value))} />
          </label>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            checked={form.tourist_tax_per_person === 1}
            onChange={e => set('tourist_tax_per_person', e.target.checked ? 1 : 0)} />
          {t('portal.manage.payouts.touristTaxPerPerson', 'La tasa turística se cobra por persona y noche (si no, solo por noche)')}
        </label>
        <p className="text-xs text-slate-400">
          {t('portal.manage.payouts.depositHint', 'Si fijas una señal, el huésped paga ese porcentaje online ahora y el resto al anfitrión.')}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <SaveButton
          onClick={onSave}
          saving={saveMut.isPending}
          isDirty={isDirty}
          label={t('portal.manage.payouts.save', 'Guardar')}
          savedLabel={t('common.saved', 'Guardado')}
        />
      </div>
    </section>
  );
}
